import type { FallbackDataFetcher, FallbackFetchContext } from "../types";

interface GammaEvent {
  id: string;
  title: string;
  slug: string;
  negRisk: boolean;
  markets: GammaMarket[];
}

interface GammaMarket {
  conditionId: string;
  question: string;
  slug: string;
  clobTokenIds: string; // JSON array
  outcomes: string; // JSON array
  endDate: string;
  description: string;
  negRisk: boolean;
  image?: string;
  icon?: string;
}

/**
 * Fetch recently created Polymarket markets via our Next.js events proxy.
 * Uses the /events/keyset endpoint with title_search for server-side
 * keyword filtering when keywords are configured.
 */
export const polymarketNewMarketFallback: FallbackDataFetcher = async (ctx: FallbackFetchContext) => {
  const { triggerPayload } = ctx;
  const keywords = triggerPayload.keywords as string[] | undefined;

  try {
    // Fetch recent events sorted by creation date via proxy.
    // The proxy forwards to /events/keyset which uses closed=false
    // instead of the legacy active=true param.
    const params = new URLSearchParams({
      closed: "false",
      limit: "20",
      order: "createdAt",
      ascending: "false",
    });

    // Pass first keyword as title_search for server-side filtering.
    // The keyset endpoint filters event titles on the server,
    // so we get relevant results even with a small limit.
    if (keywords?.length && keywords[0]) {
      params.set("title_search", keywords[0]);
    }

    const res = await fetch(`/api/polymarket/events?${params}`);
    if (!res.ok) return [];
    const events = (await res.json()) as GammaEvent[];
    if (!Array.isArray(events) || !events.length) return [];

    // Flatten to individual markets
    let markets = events.flatMap(event =>
      (event.markets ?? []).map(m => ({
        ...m,
        eventTitle: event.title,
        negRisk: m.negRisk || event.negRisk,
      })),
    );

    // Client-side filter against ALL keywords (OR logic: match any).
    // title_search only accepts one term, so we re-check the full set
    // to also match on description and additional keywords.
    if (keywords?.length) {
      const lowerKeywords = keywords.map(k => k.toLowerCase());
      markets = markets.filter(m => {
        const text = `${m.question} ${m.description ?? ""}`.toLowerCase();
        return lowerKeywords.some(kw => text.includes(kw));
      });
    }

    // Take first 5
    return markets.slice(0, 5).map(m => {
      let outcomes: string[] = [];
      let tokenIds: string[] = [];
      try {
        outcomes = JSON.parse(m.outcomes) as string[];
      } catch {
        /* empty */
      }
      try {
        tokenIds = JSON.parse(m.clobTokenIds) as string[];
      } catch {
        /* empty */
      }

      return {
        triggeredAt: new Date().toISOString(),
        market: {
          conditionId: m.conditionId,
          question: m.question,
          slug: m.slug,
          outcomes,
          eventTitle: m.eventTitle,
          tokenIds,
          endDate: m.endDate ?? null,
          negRisk: m.negRisk ?? false,
          description: m.description ?? null,
        },
        _question: m.question,
        _eventTitle: m.eventTitle,
        _outcomes: outcomes.join(", "),
        _icon: m.image || m.icon || "",
      };
    });
  } catch {
    return [];
  }
};
